import { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import {
  FormControl,
  IconButton,
  Stack,
  TextField,
  Theme,
} from '@mui/material';
import { IConnectorCredentialsFormProps } from '../connectors-types';
import TokenVerifiedTextFieldAndorment from './TokenVerifiedTextFieldAndorment';
import { v4 as uuidv4 } from 'uuid';
import { MdContentCopy as ContentCopyIcon } from 'react-icons/md';
import CustomTooltip from '../../common/components/CustomTooltip';
import CloudFormationAssumeRoleButton from './CloudFormationAssumeRoleButton';
import { isEmpty } from 'lodash';
import { CredentialsType } from '../connector-configure-types';
import { setAwsExternalIdInput } from '../store-actions/connector-config-form-store-action';
import { makeStyles } from 'tss-react/mui';

const AWSAssumeRoleForm: FunctionComponent<
  React.PropsWithChildren<IConnectorCredentialsFormProps>
> = props => {
  const {
    values,
    handleOnChange,
    isConnectorConfigured,
    errorMessage,
    credentialsType,
    connector,
    onpremAWSCredentialsRequired,
    areCredentialsExpired,
  } = props;
  const { classes } = useStyles();

  const externalIdInput = useRef<HTMLInputElement>(null);

  const copyToClipboard = useCallback(() => {
    if (externalIdInput.current) {
      externalIdInput.current.select();
      navigator.clipboard.writeText(externalIdInput.current.value);
    }
  }, []);

  useEffect(() => {
    if (isEmpty(values.awsExternalId)) {
      const uuid = uuidv4();
      setAwsExternalIdInput(uuid);
    }
  }, [values.awsExternalId]);

  const onOpenCloudFormationStack = useCallback(() => {
    const awsExternalId = values.awsExternalId;
    const awsCloudFormationInfo = connector.awsCloudFormationInfo;
    if (!awsCloudFormationInfo || !awsExternalId) {
      return null;
    }

    const { baseURL, urlParams } = awsCloudFormationInfo;
    const url = `${baseURL}${urlParams}&param_ExternalId=${awsExternalId}`;

    window.open(url, '_blank');
  }, [connector.awsCloudFormationInfo, values.awsExternalId]);

  const onOpenCloudFormationStackOrganization = useCallback(() => {
    const awsExternalId = values.awsExternalId;
    const awsCloudFormationOrganizationInfo =
      connector.awsCloudFormationOrganizationInfo;
    if (!awsCloudFormationOrganizationInfo || !awsExternalId) {
      return null;
    }

    const { baseURL, urlParams } = awsCloudFormationOrganizationInfo;
    const url = `${baseURL}${urlParams}&param_ExternalId=${awsExternalId}`;

    window.open(url, '_blank');
  }, [connector.awsCloudFormationOrganizationInfo, values.awsExternalId]);

  return (
    <>
      <Stack spacing={3}>
        <Stack
          minWidth='100%'
          flexDirection='column'
          gap={3}
          alignItems='flex-start'>
          {(credentialsType === CredentialsType.AWSAssumeRoleCloudFormation ||
            credentialsType ===
              CredentialsType.AWSAssumeRoleCloudFormationOnprem) && (
            <CloudFormationAssumeRoleButton
              onOpenCloudFormationStack={onOpenCloudFormationStack}
              isDisabled={isConnectorConfigured}
            />
          )}
          {(credentialsType === CredentialsType.AWSOrganizationConnection ||
            credentialsType ===
              CredentialsType.AWSOrganizationConnectionOnprem) && (
            <CloudFormationAssumeRoleButton
              onOpenCloudFormationStack={onOpenCloudFormationStackOrganization}
              isDisabled={isConnectorConfigured}
            />
          )}
          <FormControl className={classes.fullWidth}>
            <TextField
              autoComplete='off'
              inputRef={externalIdInput}
              size='small'
              required
              type='text'
              variant='outlined'
              label='AWS External ID'
              name='awsExternalId'
              fullWidth
              value={values.awsExternalId}
              disabled={isConnectorConfigured}
              error={!!errorMessage}
              helperText={errorMessage}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CustomTooltip title='Copy external ID'>
                    <span>
                      <IconButton
                        onClick={copyToClipboard}
                        disabled={isConnectorConfigured}
                        size='small'>
                        <ContentCopyIcon
                          color={isConnectorConfigured ? 'disabled' : 'success'}
                          fontSize='small'
                        />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                ),
                endAdornment: (
                  <TokenVerifiedTextFieldAndorment
                    isConnectorValid={!areCredentialsExpired}
                    isConnectorConfigured={isConnectorConfigured}
                  />
                ),
              }}
            />
          </FormControl>
          {onpremAWSCredentialsRequired && (
            <>
              <TextField
                size='small'
                required
                type='text'
                variant='outlined'
                label='AWS Access Key'
                name='awsAccessKey'
                value={values.awsAccessKey}
                onChange={handleOnChange}
                fullWidth
                disabled={isConnectorConfigured}
                error={!!errorMessage}
                InputProps={{
                  endAdornment: (
                    <TokenVerifiedTextFieldAndorment
                      isConnectorValid={!areCredentialsExpired}
                      isConnectorConfigured={isConnectorConfigured}
                    />
                  ),
                }}
              />
              <FormControl className={classes.fullWidth}>
                <TextField
                  size='small'
                  required
                  type='text'
                  variant='outlined'
                  label='AWS Access Secret'
                  name='awsAccessSecret'
                  value={values.awsAccessSecret}
                  onChange={handleOnChange}
                  fullWidth
                  disabled={isConnectorConfigured}
                  error={!!errorMessage}
                  InputProps={{
                    endAdornment: (
                      <TokenVerifiedTextFieldAndorment
                        isConnectorValid={!areCredentialsExpired}
                        isConnectorConfigured={isConnectorConfigured}
                      />
                    ),
                  }}
                />
              </FormControl>
            </>
          )}
          <FormControl className={classes.fullWidth}>
            <TextField
              autoComplete='off'
              size='small'
              required
              type='text'
              variant='outlined'
              label='AWS Role ARN'
              name='awsRoleArn'
              value={values.awsRoleArn}
              onChange={handleOnChange}
              fullWidth
              disabled={isConnectorConfigured}
              error={!!errorMessage}
              InputProps={{
                endAdornment: (
                  <TokenVerifiedTextFieldAndorment
                    isConnectorValid={!areCredentialsExpired}
                    isConnectorConfigured={isConnectorConfigured}
                  />
                ),
              }}
            />
          </FormControl>
        </Stack>
      </Stack>
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  fullWidth: {
    width: '100%',
  },
}));

export default AWSAssumeRoleForm;
